import React, { CSSProperties, ReactElement, ReactNode } from 'react'
import { ClickAwayListener, Popper } from '@material-ui/core'

export type PopperProps = {
    className?: string
    children?: ReactNode
    anchorEl: Nullable<HTMLElement>
    open: boolean
    setOpen: (value: boolean) => void
    isClickAwayHandled: boolean
    clickAwayCallBack?: (event: React.MouseEvent<Document, MouseEvent>) => void
    clickAwayGuard?: (event: React.MouseEvent<Document, MouseEvent>) => boolean
    overrideStyles?: CSSProperties
    transition?: boolean
    keepMounted?: boolean
}

/**
 * A generic popper component allowing custom content within it.
 *
 * @param className The class name to be applied to the popper.
 * @param children A nested list of children elements.
 * @param anchorElem The element this popper should attach too.
 * @param isOpen The flag defining the poppers open state.
 * @param setIsOpen
 * @param isClickAwayHandled
 * @param clickAwayCallBack
 * @param clickAwayGuard
 * @param overrideStyles - Override the default styles of the popper.
 * @param transition - Whether to use a transition for the popper.
 */
const PopperContent: React.FC<PopperProps> = ({
    className,
    children,
    anchorEl,
    open,
    setOpen,
    isClickAwayHandled,
    clickAwayCallBack,
    clickAwayGuard,
    overrideStyles,
    transition,
    keepMounted
}: PopperProps): ReactElement => {
    const handleOnClickAway = (event: React.MouseEvent<Document, MouseEvent>): void => {
        if (clickAwayGuard && !clickAwayGuard(event)) {
            return
        }

        // Close Popper
        setOpen(false)

        if (clickAwayCallBack) {
            clickAwayCallBack(event)
        }
    }

    return (
        <Popper
            className={`popper ${className ?? ''}`}
            open={open}
            anchorEl={anchorEl}
            placement='bottom-start'
            style={{ width: anchorEl?.offsetWidth, ...overrideStyles }}
            transition={transition}
            keepMounted={keepMounted}
        >
            {isClickAwayHandled ? (
                <ClickAwayListener onClickAway={handleOnClickAway}>
                    <div>{children}</div>
                </ClickAwayListener>
            ) : (
                <div>{children}</div>
            )}
        </Popper>
    )
}
export default PopperContent
